import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import identity from 'lodash/fp/identity';
import { useSnackbar } from 'notistack';
import { memo } from 'react';
import { useIntl } from 'react-intl';
// import { useSelector } from 'react-redux';
import { PayloadAction } from '@reduxjs/toolkit';
import { AuthException, BadRequestException, exceptionOf, ForbiddenException, Token, UnknownException } from './models';
import { forbidden, useNavigate, useQuery } from './routes';
import { useResponseInterceptor } from './services/http-hook';
import { fromAuth, useAppDispatch } from './store';
import { useConfig } from './config';
import { isInSystemMaintenance } from './models/config';

const MAX_RETRIES = 3;

const isRequestCanceled = (error: AxiosError | undefined) => (
  axios.isCancel(error)
  || error?.code === 'ERR_CANCELED'
  || error?.name === 'CanceledError'
);

const ErrorHandler = () => {
  const { replace, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { config } = useConfig();
  const dispatch = useAppDispatch();
  let retries = 0;
  let refreshTokenPromise: Promise<any> | undefined;

  const handleUnauthorized = async (error: AxiosError) => {
    const originalConfig = error.config;
    const storedToken = localStorage.getItem('k1.login') ?? 'null';
    const { refreshJwt } = JSON.parse(storedToken) as Token;
    if (refreshJwt && originalConfig) {
      const refreshResult = await handleRefreshToken(error, originalConfig);
      return refreshResult;
    }
    return error;
  };

  const handleRefreshToken = async (
    error: AxiosError,
    originalConfig: InternalAxiosRequestConfig,
  ) => {
    try {
      if (retries >= MAX_RETRIES) {
        replace(forbidden.path, replaceQuery(queryDictionary()));
        return error;
      }
      const isOriginRefreshRequest = !refreshTokenPromise;
      if (isOriginRefreshRequest) {
        try {
          refreshTokenPromise = dispatch(fromAuth.doRefreshToken());
        } catch (ex) {
          // do nothing
          return error;
        }
      }
      const response: PayloadAction<{ token: Token }> = await refreshTokenPromise;
      const { token } = response.payload;
      if (isOriginRefreshRequest) {
        await dispatch(fromAuth.doResume(token));
      }
      originalConfig.headers.Authorization = `Bearer ${token.jwt}`;
    } catch (ex) {
      // do nothing
      return error;
    } finally {
      retries += 1;
      refreshTokenPromise = undefined;
    }
    const requestAfterRefresh = await axios(originalConfig);
    return requestAfterRefresh;
  };

  useResponseInterceptor(identity, async (error, appConfig) => {
    if (isRequestCanceled(error)) {
      throw error;
    }

    const exception = exceptionOf(error);
    if (isInSystemMaintenance(appConfig)) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'error.system_maintenance_mode' }),
        { variant: 'error' },
      );
    } else if (exception.isInstanceOf(AuthException)) {
      const result = await handleUnauthorized(error);
      enqueueSnackbar(
        intl.formatMessage({ id: exception.message }),
        { variant: 'error' },
      );
      return result;
    }
    if (exception.isInstanceOf(ForbiddenException)) {
      replace(forbidden.path, replaceQuery(queryDictionary()));
      return error;
    }
    if (exception.isInstanceOf(UnknownException)) {
      enqueueSnackbar(
        intl.formatMessage({ id: exception.message }),
        { variant: 'error' },
      );
    }
    if (exception.isInstanceOf(BadRequestException)
      && !exception.message.startsWith('coupon')) {
      enqueueSnackbar(
        intl.formatMessage({ id: exception.message }),
        { variant: 'error' },
      );
    }
    return error;
  }, config);

  return null;
};

export default memo(ErrorHandler);
